import { orderReference } from '../choosechow/orderReference'
import { emitSound, type SoundCue } from './sounds'
import { parseTimestamp } from '../../lib/timestamp'

type Row = Record<string, unknown>
const short = (value: unknown) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/^(?:order|payment)_status_/, '')

/** Only positive received confirmations may request a cue. Payment requires captured status. Self-actions do not cue. */
export function commandCue(event: string, _payload: Row, response: Row): SoundCue | undefined {
  const status = short(response.status)
  switch (event) {
    case 'billing:confirm_payment:v1:requested':
      return status === 'captured' ? 'success' : undefined
    default:
      return undefined
  }
}

export function commandFeedback(event: string, payload: Row, response: Row, correlationId: string) {
  const cue = commandCue(event, payload, response)
  if (cue) emitSound(cue, `${event}:${String(response.reference || response.id || correlationId)}`)
}

export interface AppNotification {
  id: string
  cue: SoundCue
  key: string
  groupKey?: string
  title: string
  body: string
  url: string
  tone?: 'info' | 'success' | 'warning' | 'danger'
}

/** Describe confirmed progress using the order's recorded fulfilment method. */
function orderNotificationCopy(
  row: Row,
  chef: boolean,
  status: string,
  id: string,
  ref: string,
  totalAmount: string
) {
  const pickup =
    String(row.delivery_choice ?? '')
      .toLowerCase()
      .replace(/^delivery_choice_/, '') === 'self_pickup'
  let title = `Order #${ref} updated`
  let body = `Status changed to ${status.replace(/_/g, ' ')}.`
  let tone: AppNotification['tone'] = 'success'
  const url = chef ? `/orders/${id}` : '/orders'
  if (status === 'paid') {
    title = chef ? `New paid order (₦${totalAmount})` : `Payment received for order #${ref}`
    body = chef
      ? 'Accept this order to begin preparation.'
      : 'Your order has been sent to the kitchen.'
  } else if (status === 'accepted') {
    title = `Order #${ref} accepted`
    body = chef ? 'Order accepted. It is in your prep queue.' : 'The chef has accepted your order.'
  } else if (status === 'preparing') {
    title = `Preparing order #${ref}`
    body = chef
      ? 'Preparation started. Mark the meal ready when it is packed.'
      : 'The kitchen has started preparing your meal.'
  } else if (status === 'ready') {
    title = pickup ? `Order #${ref} ready for pickup` : `Order #${ref} ready`
    body = chef
      ? 'Confirm collection after handing the meal to the customer.'
      : pickup
        ? `Collect your meal${row.pickup_address ? ` from ${String(row.pickup_address)}` : ' from the kitchen'}.`
        : 'Your meal is freshly packed.'
  } else if (status === 'in_delivery') {
    title = `Order #${ref} on the way`
    body = 'The kitchen has confirmed the delivery handoff.'
  } else if (status === 'completed') {
    title = pickup ? `Order #${ref} collected` : `Order #${ref} completed`
    body = chef
      ? 'Collection recorded. This order has left your prep queue.'
      : 'The kitchen confirmed you received your meal. Enjoy it! You can now rate your dishes.'
  } else if (status === 'cancelled') {
    title = `Order #${ref} cancelled`
    body = 'This order will no longer be prepared. Open your orders for details.'
    tone = 'danger'
  } else if (status === 'refunded') {
    title = `Order #${ref} refunded`
    body = 'The refund has been recorded. Open your orders for details.'
  }
  return { title, body, tone, url }
}

/** Baselines and owner checks prevent sign-in, reconnect, and unrelated rows from sounding. */
export class ProjectionCueTracker {
  private initialized = false
  private orders = new Map<string, string>()
  private messages = new Set<string>()
  private since = Date.now() - 30_000

  scan(
    orderRows: readonly Row[],
    messageRows: readonly Row[],
    profileId: string,
    chef: boolean
  ): AppNotification[] {
    const norm = (id: unknown) =>
      String(id ?? '')
        .trim()
        .toLowerCase()
    const currentProfileId = norm(profileId)
    const mine = orderRows.filter(
      (r) => norm(r[chef ? 'merchant_profile_id' : 'customer_profile_id']) === currentProfileId
    )
    const ids = new Set(mine.map((r) => String(r.id)))

    // Mark all own messages as seen so they never trigger cues or notices.
    for (const row of messageRows) {
      if (norm(row.sender_profile_id) === currentProfileId) {
        this.messages.add(String(row.id))
      }
    }

    const messages = messageRows.filter(
      (r) => ids.has(String(r.order_id)) && norm(r.sender_profile_id) !== currentProfileId
    )
    const notifications: AppNotification[] = []

    for (const row of mine.slice(-512)) {
      const id = String(row.id)
      const status = short(row.status)
      const previous = this.orders.get(id)
      const rowTime = parseTimestamp(row.updated_at || row.created_at)
      const recent = rowTime !== null ? rowTime >= this.since : false
      const ref = orderReference(row, '')
      const totalAmount = (Number(row.total_minor ?? 0) / 100).toLocaleString('en-NG', {
        maximumFractionDigits: 2,
      })

      if (
        this.initialized &&
        recent &&
        previous !== status &&
        ['paid', 'ready', 'in_delivery', 'completed', 'cancelled', 'refunded'].includes(status)
      ) {
        const { title, body, tone, url } = orderNotificationCopy(
          row,
          chef,
          status,
          id,
          ref,
          totalAmount
        )

        notifications.push({
          id: `order:${id}:${status}`,
          // A paid order is the chef's cue to start cooking, so it gets the
          // distinct bell. Every other status change is the ordinary chime.
          cue: chef && status === 'paid' ? 'new_order' : 'order',
          key: `order:${id}:${status}`,
          groupKey: `order:${id}`,
          title,
          body,
          url,
          tone,
        })
      }
      this.orders.set(id, status)
    }

    for (const row of messages.slice(-512)) {
      const id = String(row.id)
      const orderId = String(row.order_id ?? '')
      const rowTime = parseTimestamp(row.created_at)
      const recent = rowTime !== null ? rowTime >= this.since : false
      const senderClass = String(row.sender_class ?? '')
      const senderLabel =
        senderClass === 'chef' ? 'Chef' : senderClass === 'rider' ? 'Rider' : 'Customer'
      const bodySnippet = String(row.body ?? 'New message received').slice(0, 75)

      if (this.initialized && !this.messages.has(id) && recent) {
        notifications.push({
          id: `message:${id}`,
          cue: 'message',
          key: `message:${id}`,
          title: `New message from ${senderLabel}`,
          body: bodySnippet,
          url: `/inbox?order=${encodeURIComponent(orderId)}`,
          tone: 'info',
        })
      }
      this.messages.add(id)
    }

    this.orders = new Map([...this.orders].slice(-512))
    this.messages = new Set([...this.messages].slice(-512))
    this.initialized = true
    return notifications
  }
}
